import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { trpc } from '../api/trpc';
import { ConnectorDeleteControl } from './ConnectorDeleteControl';

interface Permission {
  id: string;
  value: string;
  displayName: string;
  description: string;
  shortCapability: string;
  tier: string;
  category: string;
  isRequired: boolean;
  isWrite: boolean;
}

interface Props {
  connector: {
    id: string;
    name: string;
    config: Record<string, unknown>;
  };
  onClose: () => void;
  onSaved: () => void;
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12, borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--color-text-primary)', fontFamily: mono ? 'var(--font-mono)' : undefined, fontSize: mono ? 11 : undefined, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

export function MicrosoftConnectorSettings({ connector, onClose, onSaved }: Props) {
  const cfg = connector.config;
  // Spec §625a: read live granted state, not just the catalog. Single query
  // returns catalog + selected + granted so the UI never has to infer.
  const stateQuery = trpc.connectors.provisioning.microsoft.getPermissionState.useQuery({ connectorId: connector.id });
  const refreshMut = trpc.connectors.provisioning.microsoft.refreshGrantedPermissions.useMutation();
  const updatePermsMut = trpc.connectors.provisioning.microsoft.updatePermissions.useMutation();
  const testMut = trpc.connectors.testConnection.useMutation();
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  const allPermissions = (stateQuery.data?.catalog ?? []) as Permission[];
  const grantedFromServer = stateQuery.data?.granted ?? null;
  const selectedFromServer = stateQuery.data?.selected ?? [];
  const isAutoProvisioned = cfg['provisioningId'] != null;
  const authMode = (cfg['authMode'] as string) ?? 'client_secret';

  // Auto-trigger reconciliation once when the record exists but the granted
  // column is empty (legacy connector predating §622a, or post-consent
  // reconciliation that failed). Idempotent — guarded by a ref so we never
  // call it twice in the same mount.
  const refreshAttempted = useRef(false);
  useEffect(() => {
    if (refreshAttempted.current) return;
    if (grantedFromServer === null) return; // no provisioning record at all — manual setup, can't refresh
    if (grantedFromServer.length > 0) return; // already reconciled
    refreshAttempted.current = true;
    refreshMut.mutateAsync({ connectorId: connector.id })
      .then(() => stateQuery.refetch())
      .catch(() => setRefreshFailed(true));
  }, [grantedFromServer, connector.id, refreshMut, stateQuery]);

  // The "ground truth" set the checkboxes should initialize from:
  //   1. granted (Microsoft's actual response) — preferred
  //   2. selected (the admin's last request) — fallback when refresh fails
  //   3. all required permissions — fallback when nothing is known yet
  const baselineIds = useMemo<Set<string>>(() => {
    if (grantedFromServer && grantedFromServer.length > 0) {
      return new Set(grantedFromServer);
    }
    if (refreshFailed && selectedFromServer.length > 0) {
      return new Set(selectedFromServer);
    }
    // No reliable baseline yet — only required permissions are pre-checked
    // (they always must be in the set; the admin can add the rest).
    return new Set(allPermissions.filter(p => p.isRequired).map(p => p.id));
  }, [grantedFromServer, selectedFromServer, refreshFailed, allPermissions]);

  // Track current checkbox state. Re-syncs when baseline changes (e.g. after
  // a refetch) but preserves user toggles within a session via the dirty flag.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(baselineIds));
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) setSelectedIds(new Set(baselineIds));
  }, [baselineIds, dirty]);

  // Group by category
  const grouped: Record<string, Permission[]> = {};
  for (const p of allPermissions) {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category]!.push(p);
  }

  // Real set-difference (vs. the original `size` length-check). True when
  // current selection differs from the baseline in either direction —
  // additions (e.g. opting into a new catalog permission) or removals.
  const hasChanges = useMemo(() => {
    if (selectedIds.size !== baselineIds.size) return true;
    for (const id of selectedIds) {
      if (!baselineIds.has(id)) return true;
    }
    return false;
  }, [selectedIds, baselineIds]);

  // Permissions in the catalog but NOT in the granted set are "new" — they
  // weren't part of the Entra app reg when the connector was provisioned.
  // Used to render the "New — re-consent to enable" badge per §625a.
  const isNewPermission = useCallback((id: string): boolean => {
    if (!grantedFromServer) return false;
    return !grantedFromServer.includes(id);
  }, [grantedFromServer]);

  const handleToggle = useCallback((id: string, isRequired: boolean) => {
    if (isRequired) return;
    setDirty(true);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleSaveAndReconsent = useCallback(async () => {
    try {
      const result = await updatePermsMut.mutateAsync({
        connectorId: connector.id,
        selectedPermissionIds: Array.from(selectedIds),
      });
      // Redirect to admin consent
      window.location.href = result.adminConsentUrl;
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : 'Failed to update permissions' });
    }
  }, [connector.id, selectedIds, updatePermsMut]);

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testMut.mutateAsync({ connectorId: connector.id });
      setTestResult(result as { ok: boolean; message: string });
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  }, [connector.id, testMut]);

  // Re-run the connection test as soon as the panel opens, so a failed
  // connector shows its failure reason without the user clicking anything.
  const autoTested = useRef(false);
  useEffect(() => {
    if (autoTested.current) return;
    autoTested.current = true;
    void handleTest();
  }, [handleTest]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0' }}>
      {/* App details */}
      <div style={{ padding: 14, borderRadius: 8, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Connector details</div>
        {cfg['tenantId'] ? <InfoRow label="Entra Tenant ID" value={cfg['tenantId'] as string} mono /> : null}
        {cfg['clientId'] ? <InfoRow label="App (Client) ID" value={cfg['clientId'] as string} mono /> : null}
        <InfoRow label="Auth mode" value={isAutoProvisioned ? 'Auto-provisioned' : authMode === 'certificate' ? 'Certificate (KMS)' : 'Client secret'} />
        {cfg['secretExpiresAt'] ? <InfoRow label="Secret expires" value={new Date(cfg['secretExpiresAt'] as string).toLocaleDateString()} /> : null}
        {cfg['certExpiresAt'] ? <InfoRow label="Certificate expires" value={new Date(cfg['certExpiresAt'] as string).toLocaleDateString()} /> : null}
        <InfoRow label="National cloud" value={(cfg['nationalCloud'] as string) ?? 'global'} />
      </div>

      {/* Editable permissions */}
      {allPermissions.length > 0 && (
        <div style={{ padding: 14, borderRadius: 8, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Permissions</div>

          {grantedFromServer === null && (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              This connector was set up manually — Cade can't manage its permissions from here. Make changes directly in the Entra portal.
            </div>
          )}

          {refreshFailed && (
            <div style={{ fontSize: 11, padding: '6px 8px', borderRadius: 4, background: 'rgba(220,160,0,0.08)', border: '1px solid var(--sev-medium)', color: 'var(--sev-medium)', marginBottom: 8 }}>
              Couldn't verify your live Microsoft permissions. Showing the last requested set; click "Save & Re-consent" to push the latest selection to Microsoft.
            </div>
          )}

          {refreshMut.isPending && grantedFromServer !== null && grantedFromServer.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              Verifying your Microsoft permissions…
            </div>
          )}

          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {Object.entries(grouped).map(([category, perms]) => (
              <div key={category} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                  {category.replace(/_/g, ' ')}
                </div>
                {perms.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '3px 0', cursor: p.isRequired ? 'not-allowed' : 'pointer', opacity: p.isRequired ? 0.7 : 1 }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      disabled={p.isRequired}
                      onChange={() => handleToggle(p.id, p.isRequired)}
                      style={{ marginTop: 2, width: 'auto' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>
                        {p.value}
                        {p.isWrite && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--sev-medium)', color: '#fff' }}>Write</span>}
                        {p.isRequired && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--color-text-muted)' }}>Required</span>}
                        {isNewPermission(p.id) && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--sev-low)', color: '#fff' }}>New — re-consent to enable</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{p.shortCapability}</div>
                    </div>
                  </label>
                ))}
              </div>
            ))}
          </div>

          {grantedFromServer !== null && (
            <button
              type="button" className="btn-primary"
              onClick={() => { void handleSaveAndReconsent(); }}
              disabled={updatePermsMut.isPending || !hasChanges}
              style={{ width: '100%', marginTop: 10, opacity: hasChanges ? 1 : 0.5 }}
              title={hasChanges ? '' : 'No changes — toggle a permission to enable.'}
            >
              {updatePermsMut.isPending ? 'Updating...' : 'Save & Re-consent in Microsoft'}
            </button>
          )}
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div style={{ padding: 10, borderRadius: 6, fontSize: 12, background: testResult.ok ? 'rgba(0,180,0,0.08)' : 'rgba(220,0,0,0.08)', border: `1px solid ${testResult.ok ? 'var(--sev-success)' : 'var(--sev-critical)'}` }}>
          {testResult.message}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn-secondary" onClick={() => { void handleTest(); }} disabled={testing} style={{ flex: 1 }}>
          {testing ? 'Testing...' : 'Test connection'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      <ConnectorDeleteControl
        connectorId={connector.id}
        connectorLabel={connector.name}
        onDeleted={onSaved}
      />
    </div>
  );
}
